/**
 * A radio choice laid out as a card (U40): the shared `RadioGroupItem`, a bold label, an optional note and detail
 * lines, the chosen card outlined. For choices that have consequences — the access tier, a container's public access —
 * inside a `RadioGroup`, which gives the arrow-key navigation and the `radiogroup` role.
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { RadioGroupItem } from './radio-group';

type RadioCardProps = {
  value: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  /** Hover text of the card and its radio (e.g. why the choice is unavailable). */
  title?: string;
  /** A short muted note beside the label ("Already this tier"). */
  note?: ReactNode;
  /** What the choice means, under the label. */
  detail?: ReactNode;
  children?: ReactNode;
  testId?: string;
};

export function RadioCard({ value, label, checked, disabled, title, note, detail, children, testId }: RadioCardProps) {
  return (
    <label
      className={cn('block rounded border p-2 text-sm', checked ? 'border-primary bg-primary/5' : 'border-border', disabled && 'opacity-70')}
      title={title}
      data-testid={testId}
    >
      <span className="flex items-center gap-2">
        <RadioGroupItem value={value} aria-label={label} title={title} disabled={disabled} />
        <span className="font-medium">{label}</span>
        {note && <span className="text-xs text-muted-foreground">{note}</span>}
      </span>
      {detail && <span className="ml-6 block text-xs text-muted-foreground">{detail}</span>}
      {children}
    </label>
  );
}
